import * as Application from "expo-application";
import * as Device from "expo-device";
import { router } from "expo-router";
import type { PropsWithChildren, Ref } from "react";
import React, {
  createContext,
  useCallback,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Platform } from "react-native";

import {
  TV_CONNECT_MDNS_DISCOVERY_DOMAIN,
  TV_CONNECT_MDNS_SERVICE_TYPE,
} from "$modules/expo-tv-connect";
import type {
  Capabilities,
  HostInboundMessageEvent,
  StartHostingOptions,
} from "$modules/expo-tv-connect-host";
import {
  ExpoTvConnectHostModule,
  PlatformKeyCode,
  TV_CONNECT_PROTOCOL_VERSION,
} from "$modules/expo-tv-connect-host";
import type { ModalRef } from "$src/core/components/atoms/Modal/types";
import ConfirmationModal from "$src/core/components/molecules/ConfirmationModal/ConfirmationModal";
import TvConnectConnectedToastOverlay from "$src/core/components/organisms/TvConnectConnectedToastOverlay/TvConnectConnectedToastOverlay";
import { TV_CONNECT_CONNECTED_TOAST_MS } from "$src/core/constants/tv-connect";
import useAppStateEffect from "$src/core/hooks/use-app-state-effect";
import useAuth from "$src/core/hooks/use-auth";
import useMenu from "$src/core/hooks/use-menu";
import useStorageState from "$src/core/hooks/use-storage-state";
import useTranslation from "$src/core/hooks/use-translation";
import Logging from "$src/core/services/logging/logging";
import wait from "$src/core/utils/async/wait";
import getStableDeviceId from "$src/core/utils/device/get-stable-device-id";
import CustomPlatform from "$src/core/utils/platform/platform";

const HOST_SUPPORTED_KEYS: PlatformKeyCode[] = [
  PlatformKeyCode.Back,
  PlatformKeyCode.Play,
  PlatformKeyCode.Pause,
  PlatformKeyCode.Stop,
  PlatformKeyCode.Rewind,
  PlatformKeyCode.FastForward,
];

const HOST_PLATFORM = Platform.isTV
  ? Platform.OS === "ios"
    ? "tvos"
    : "androidtv"
  : Platform.OS;

const buildCapabilities = (): Capabilities => ({
  protocolVersion: TV_CONNECT_PROTOCOL_VERSION,
  appVersion: Application.nativeApplicationVersion ?? "",
  platform: HOST_PLATFORM,
  supportedKeys: HOST_SUPPORTED_KEYS,
  features: [],
  volumeControl: true,
});

const buildHostingOptions = (code: string | null): StartHostingOptions => {
  const id = getStableDeviceId();

  const txt: Record<string, string> = {
    v: "1",
    ready: "1",
    platform: HOST_PLATFORM,
    name: Device.deviceName?.trim() || id,
  };

  if (code) {
    txt.code = code;
  }

  return {
    serviceName: id,
    txt,
  };
};

type ConnectedToastRef = {
  present: (name?: string | null) => void;
};

const ConnectedToast = ({ ref }: { ref?: Ref<ConnectedToastRef> }) => {
  const [name, setName] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useImperativeHandle(ref, () => ({
    present: (deviceName) => {
      setName(deviceName ?? null);
      setIsVisible(true);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        timeoutRef.current = null;
      }, TV_CONNECT_CONNECTED_TOAST_MS);
    },
  }));

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  if (!isVisible) return null;

  return <TvConnectConnectedToastOverlay name={name} />;
};

type TvConnectHostContextType = {
  setPairingCode: (code: string | null) => void;
};

export const TvConnectHostContext =
  createContext<TvConnectHostContextType | null>(null);

export const TvConnectHostProvider = ({ children }: PropsWithChildren) => {
  const {
    status: authStatus,
    profileId: hostProfileId,
    startPairing,
    checkPairing,
    selectProfile,
  } = useAuth();
  const { t } = useTranslation();
  const menu = useMenu();

  const [knownClientIds, setKnownClientIds] = useStorageState(
    "tv-connect.known-client-ids",
    [],
  );

  const processingPairingCodeRef = useRef<string | null>(null);
  const [pairingCode, setPairingCode] = useState<string | null>(null);

  const pendingStartHostingRef = useRef<Promise<void> | null>(null);
  const pendingStopHostingRef = useRef<Promise<void> | null>(null);

  const pendingClientIdRef = useRef<string | null>(null);

  const confirmModalRef = useRef<ModalRef | null>(null);

  const connectedToastRef = useRef<ConnectedToastRef | null>(null);

  const clearPendingRemote = useCallback(() => {
    pendingClientIdRef.current = null;
    confirmModalRef.current?.dismiss();
  }, []);

  const handleAcceptRemote = useCallback(
    (clientId: string) => {
      if (!clientId) return;

      clearPendingRemote();

      try {
        ExpoTvConnectHostModule.acceptRemote(clientId);

        setKnownClientIds((_knownClientIds) => {
          const existing = _knownClientIds ?? [];
          if (existing.includes(clientId)) return existing;
          return [...existing, clientId];
        });
      } catch (error) {
        Logging.error("Failed to accept remote", { error });
      }
    },
    [clearPendingRemote, setKnownClientIds],
  );

  const handleRejectRemote = useCallback(() => {
    const clientId = pendingClientIdRef.current;
    if (!clientId) return;

    clearPendingRemote();

    try {
      ExpoTvConnectHostModule.rejectRemote(clientId);
    } catch (error) {
      Logging.error("Failed to reject remote", { error });
    }
  }, [clearPendingRemote]);

  const handleConfirmRemote = useCallback(async () => {
    const clientId = pendingClientIdRef.current;
    if (clientId) handleAcceptRemote(clientId);
  }, [handleAcceptRemote]);

  useLayoutEffect(() => {
    try {
      Logging.debug("[TvConnectHost] Configuring");
      ExpoTvConnectHostModule.configure({
        mdnsServiceType: TV_CONNECT_MDNS_SERVICE_TYPE,
        mdnsDiscoveryDomain: TV_CONNECT_MDNS_DISCOVERY_DOMAIN,
      });
    } catch (error) {
      Logging.error("[TvConnectHost] Failed to configure", { error });
    }
  }, []);

  const startHosting = useCallback(async (options: StartHostingOptions) => {
    if (pendingStartHostingRef.current) {
      return pendingStartHostingRef.current;
    }

    if (pendingStopHostingRef.current) {
      await pendingStopHostingRef.current;
      await wait(400);
    }

    Logging.debug("[TvConnectHost] Starting hosting");

    const promise = ExpoTvConnectHostModule.startHosting(options);
    pendingStartHostingRef.current = promise;

    try {
      return await promise;
    } finally {
      if (pendingStartHostingRef.current === promise) {
        pendingStartHostingRef.current = null;
      }
    }
  }, []);

  const stopHosting = useCallback(async () => {
    if (pendingStopHostingRef.current) {
      return pendingStopHostingRef.current;
    }

    if (pendingStartHostingRef.current) {
      await pendingStartHostingRef.current;
      await wait(400);
    }

    Logging.debug("[TvConnectHost] Stopping hosting");

    const promise = ExpoTvConnectHostModule.stopHosting();
    pendingStopHostingRef.current = promise;

    try {
      return await promise;
    } finally {
      if (pendingStopHostingRef.current === promise) {
        pendingStopHostingRef.current = null;
      }
    }
  }, []);

  /* Report host errors */
  useEffect(() => {
    const subscription = ExpoTvConnectHostModule.addListener(
      "onError",
      (event) => {
        Logging.error("TvConnectHost/onError", { event });
      },
    );

    return () => subscription.remove();
  }, []);

  const onRemoteRequest = useEffectEvent((event: { clientId: string }) => {
    const { clientId } = event;

    Logging.debug("[TvConnectHost] Remote request", { id: clientId });

    if (knownClientIds?.includes(clientId)) {
      handleAcceptRemote(clientId);
    } else {
      pendingClientIdRef.current = clientId;
      confirmModalRef.current?.present();
    }
  });

  /* Handle remote connection requests */
  useEffect(() => {
    const subscription = ExpoTvConnectHostModule.addListener(
      "onRemoteRequest",
      onRemoteRequest,
    );

    return () => subscription.remove();
  }, [knownClientIds]);

  const startPairingHandshake = useCallback(
    async (clientId: string) => {
      const broadcastPairing = await startPairing();
      if (!broadcastPairing) return;

      processingPairingCodeRef.current = broadcastPairing.code;

      try {
        ExpoTvConnectHostModule.send(clientId, {
          type: "ack",
          payload: { code: broadcastPairing.code },
        });
      } catch (error) {
        Logging.error("Failed to send pairing code", { error });
      }
    },
    [startPairing],
  );

  const handlePairingHandshakeCompleted = useEffectEvent(
    async ({ profileId, name }: { profileId?: string; name?: string }) => {
      // Only a device that was not signed in yet needs the navigation reset to
      // leave the login screen. Pairing an already signed-in device is a
      // re-pair mid-session, and replacing the stack there drops the user out
      // of whatever they were doing — out of the player, back to the storefront.
      const wasSignedIn = authStatus === "authenticated" && !!hostProfileId;

      // The remote has acknowledged — surface it on screen regardless of the
      // pairing-code exchange outcome below.
      connectedToastRef.current?.present(name);

      try {
        const code = processingPairingCodeRef.current;
        if (!code) return;

        const success = await checkPairing(code);
        if (!success) return;

        if (profileId) selectProfile(profileId);
        if (!wasSignedIn) router.replace("/");
      } catch (error) {
        Logging.error("Failed to check pairing", { error });
      } finally {
        processingPairingCodeRef.current = null;
      }
    },
  );

  const handleRouteMessage = useEffectEvent(
    (payload: {
      routeKey: "storefront" | "search";
      routeParams?: Record<string, string>;
    }) => {
      const { routeKey, routeParams } = payload;

      const item = menu?.routable.find((tab) => tab.slug === routeKey);
      if (!item) {
        Logging.error("No routable menu item for route key", { routeKey });
        return;
      }

      if (router.canDismiss()) {
        router.dismissAll();
      }

      const query = new URLSearchParams(routeParams).toString();

      router.navigate(
        `/(slot-${item.slot})/${item.slug}${query ? `?${query}` : ""}`,
      );
    },
  );

  const handleRemoteConnected = useEffectEvent((clientId: string) => {
    Logging.debug("[TvConnectHost] Remote connected", { id: clientId });

    try {
      ExpoTvConnectHostModule.send(clientId, {
        type: "capabilities",
        payload: buildCapabilities(),
      });
    } catch (error) {
      Logging.error("Failed to announce host capabilities", { error });
    }

    startPairingHandshake(clientId).catch((error) => {
      Logging.error("Failed to start pairing handshake", { error });
    });
  });

  /* Announce capabilities and offer pairing once the remote is accepted */
  useEffect(() => {
    const subscription = ExpoTvConnectHostModule.addListener(
      "onRemoteConnected",
      (event) => handleRemoteConnected(event.clientId),
    );

    return () => subscription.remove();
  }, []);

  /* Clean up pending state when the remote disconnects */
  useEffect(() => {
    const subscription = ExpoTvConnectHostModule.addListener(
      "onRemoteDisconnected",
      (event) => {
        processingPairingCodeRef.current = null;

        if (pendingClientIdRef.current === event.clientId) {
          clearPendingRemote();
        }
      },
    );

    return () => subscription.remove();
  }, [clearPendingRemote]);

  /* Handle messages from connected remote */
  useEffect(() => {
    const subscription = ExpoTvConnectHostModule.addListener(
      "onMessage",
      (message: HostInboundMessageEvent) => {
        Logging.debug("[TvConnectHost] Message received", { message });

        // Ignore messages from a remote still awaiting approval
        if (message.clientId === pendingClientIdRef.current) return;

        if (message.type === "ack") {
          handlePairingHandshakeCompleted({
            profileId: message.payload?.profileId,
            name: message.payload?.name,
          }).catch((error) => {
            Logging.error("Failed to complete pairing handshake", { error });
          });
          return;
        }

        if (message.type === "route") {
          handleRouteMessage(message.payload);
          return;
        }

        if (message.type === "play") {
          const { contentId, contentType, trailer } = message.payload;

          if (router.canDismiss()) {
            router.dismissAll();
          }

          router.navigate(`/${contentType}/${contentId}`);
          router.navigate(
            `/${contentType}/${contentId}/play${trailer ? "?trailer=true" : ""}`,
          );
          return;
        }

        if (
          message.type === "buttonPress" &&
          message.payload.key === PlatformKeyCode.Back
        ) {
          if (router.canGoBack()) {
            router.back();
          } else {
            router.replace("/");
          }
        }
      },
    );

    return () => subscription.remove();
  }, []);

  /* Always discoverable while foregrounded */
  useAppStateEffect(
    (appState) => {
      const abortController = new AbortController();
      const { signal } = abortController;

      const execute = async () => {
        if (signal.aborted) return;

        const shouldHost = appState !== "background";

        if (shouldHost) {
          const options = buildHostingOptions(pairingCode);

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (signal.aborted) return;

          await startHosting(options);
        } else {
          await stopHosting();
        }
      };

      execute().catch((error) => {
        if (signal.aborted) return;

        Logging.error("Failed to start/stop TV connect host", { error });
      });

      return () => {
        abortController.abort();
      };
    },
    [pairingCode, startHosting, stopHosting],
  );

  /* Stop hosting when the context unmounts */
  useEffect(
    () => () => {
      stopHosting().catch((error) => {
        Logging.error("Failed to stop hosting", { error });
      });
    },
    [stopHosting],
  );

  const value = useMemo(
    () => ({
      setPairingCode,
    }),
    [],
  );

  return (
    <TvConnectHostContext.Provider value={value}>
      {children}

      <ConfirmationModal
        ref={confirmModalRef}
        title={t("TV_CONNECT/HOST/CONFIRM_TITLE")}
        description={t("TV_CONNECT/HOST/CONFIRM_DESCRIPTION")}
        cancelLabel={t("TV_CONNECT/HOST/DENY")}
        confirmLabel={t("TV_CONNECT/HOST/ALLOW")}
        onCancel={handleRejectRemote}
        onConfirm={handleConfirmRemote}
      />

      <ConnectedToast ref={connectedToastRef} />
    </TvConnectHostContext.Provider>
  );
};

const INERT_HOST: TvConnectHostContextType = { setPairingCode: () => {} };

// A platform that cannot host still has to supply the context, because
// `useTvConnectHost` throws without one and `LoginPage.tv` reads it on mount.
const InertTvConnectHostProvider = ({ children }: PropsWithChildren) => (
  <TvConnectHostContext.Provider value={INERT_HOST}>
    {children}
  </TvConnectHostContext.Provider>
);

export default CustomPlatform.select<React.FC<PropsWithChildren>>({
  // The Android host module is a stub, so hosting there throws on every call.
  androidTV: InertTvConnectHostProvider,
  tv: TvConnectHostProvider,
  default: InertTvConnectHostProvider,
});
